import { readCookieConsent } from "./cookie-consent";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
    __hommerFitnessAnalytics?: {
      configured: boolean;
      scriptInjected: boolean;
      lastTrackedPath: string | null;
    };
  }
}

const GA_MEASUREMENT_ID = process.env.GATSBY_GA_MEASUREMENT_ID;
const GTAG_SCRIPT_ID = "hommerfitness-gtag-script";
const GTAG_INLINE_SCRIPT_ID = "hommerfitness-gtag-inline";

function isBrowser() {
  return typeof window !== "undefined";
}

function getAnalyticsState() {
  if (!isBrowser()) {
    return null;
  }

  if (!window.__hommerFitnessAnalytics) {
    window.__hommerFitnessAnalytics = {
      configured: false,
      scriptInjected: false,
      lastTrackedPath: null,
    };
  }

  return window.__hommerFitnessAnalytics;
}

function injectAnalyticsScripts() {
  const state = getAnalyticsState();

  if (!state || !GA_MEASUREMENT_ID) {
    return false;
  }

  if (state.scriptInjected) {
    return true;
  }

  if (
    document.getElementById(GTAG_INLINE_SCRIPT_ID) ||
    document.getElementById(GTAG_SCRIPT_ID)
  ) {
    state.scriptInjected = true;
    state.configured = true;
    return true;
  }

  const inlineScript = document.createElement("script");
  inlineScript.id = GTAG_INLINE_SCRIPT_ID;
  inlineScript.textContent = [
    "window.dataLayer=window.dataLayer||[];",
    "function gtag(){dataLayer.push(arguments);}",
    "gtag('consent','default',{'ad_personalization':'denied','ad_storage':'denied','ad_user_data':'denied','analytics_storage':'denied'});",
    "gtag('consent','update',{'ad_personalization':'denied','ad_storage':'denied','ad_user_data':'denied','analytics_storage':'granted'});",
    "gtag('js',new Date());",
    "gtag('config','" +
      GA_MEASUREMENT_ID +
      "',{'anonymize_ip':true,'send_page_view':false,'cookie_domain':'none'});",
  ].join("");
  document.body.appendChild(inlineScript);

  const externalScript = document.createElement("script");
  externalScript.id = GTAG_SCRIPT_ID;
  externalScript.async = true;
  externalScript.src =
    "https://www.googletagmanager.com/gtag/js?id=" + GA_MEASUREMENT_ID;
  document.body.appendChild(externalScript);

  state.scriptInjected = true;
  state.configured = true;

  return true;
}

export function setAnalyticsConsent(granted: boolean) {
  if (!isBrowser()) {
    return false;
  }

  const consent = readCookieConsent();

  if (granted && (!consent.answered || !consent.analytics)) {
    return false;
  }

  if (granted) {
    const result = injectAnalyticsScripts();

    if (window.gtag) {
      window.gtag("consent", "update", {
        ad_personalization: "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        analytics_storage: "granted",
      });
    }

    if (!result && !window.gtag) {
      return false;
    }
  } else if (window.gtag) {
    window.gtag("consent", "update", {
      ad_personalization: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      analytics_storage: "denied",
    });

    const state = getAnalyticsState();
    if (state) {
      state.lastTrackedPath = null;
    }
  }

  return true;
}

function getCurrentPath() {
  if (!isBrowser()) {
    return "/";
  }

  return (
    window.location.pathname + window.location.search + window.location.hash
  );
}

export function trackPageView(path = getCurrentPath()) {
  const consent = readCookieConsent();
  const state = getAnalyticsState();

  if (!consent.answered || !consent.analytics || !state || !state.configured) {
    return false;
  }

  if (!window.gtag) {
    return false;
  }

  if (state.lastTrackedPath === path) {
    return false;
  }

  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
    send_to: GA_MEASUREMENT_ID,
  });
  state.lastTrackedPath = path;

  return true;
}

export function initializeAnalyticsFromConsent() {
  const consent = readCookieConsent();

  if (!consent.answered || !consent.analytics) {
    return false;
  }

  const initialized = setAnalyticsConsent(true);

  if (!initialized) {
    return false;
  }

  trackPageView();

  return true;
}
